import React, { SyntheticEvent, useCallback, useEffect, useState } from 'react';

import { Helmet } from 'react-helmet';

import { Container, Stack, Tab, Tabs } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import { preRegisterSpotlightRoutes } from '~/components/Common/Spotlight/SpotlightProvider';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';

import { isAdmin } from '~/utils/adminPermission';
import featureToggle from '~/utils/featureToggle';

import MakeMeAnOfferButton from '../../components/MakeMeAnOffer/MakeMeAnOfferButton';
import AccommodationProperties from '../Properties/AccommodationProperties';

export enum AccommodationTabs {
  PROPERTIES = 'properties',
  UNMAPPED_SUPPLIERS = 'unmapped_suppliers',
  POTENTIAL_MAPPINGS = 'potential_mappings',
  FACILITIES = 'facilities',
  AMENITIES = 'amenities',
  MMAO_OFFERS = 'mmao_offers',
  BUNDLES = 'bundles',
  DYNAMIC_CONFIGURATIONS = 'dynamic_configurations',
}

preRegisterSpotlightRoutes([
  {
    path: `/accommodation/properties#${AccommodationTabs.PROPERTIES}`,
    label: 'Accommodation / Properties',
    section: 'Accommodation',
  },
  {
    path: `/accommodation/properties#${AccommodationTabs.POTENTIAL_MAPPINGS}`,
    label: 'Accommodation / Potential Mappings',
    section: 'Accommodation',
  },
  {
    path: `/accommodation/properties#${AccommodationTabs.UNMAPPED_SUPPLIERS}`,
    label: 'Accommodation / Unmapped Suppliers',
    section: 'Accommodation',
  },
  {
    path: `/accommodation/properties#${AccommodationTabs.FACILITIES}`,
    label: 'Accommodation / Facilities',
    section: 'Accommodation',
  },
  {
    path: `/accommodation/properties#${AccommodationTabs.AMENITIES}`,
    label: 'Accommodation / Amenities',
    section: 'Accommodation',
  },
]);

export default function AccommodationHomePage() {
  const [selectedTab, setSelectedTab] = useState(
    (window.location.hash?.replace('#', '') as AccommodationTabs) ?? AccommodationTabs.PROPERTIES,
  );
  const { push } = useAppNavigation();

  const { user } = useCurrentUser();
  const userIsAdmin = isAdmin(user);
  const isMmaoEnabled = featureToggle.availableToShow('MAKE_ME_AN_OFFER_ENABLED');
  const isMakeMeAnOfferButtonVisible = isMmaoEnabled;

  useEffect(() => {
    if (window.location.hash?.replace('#', '') === '') {
      window.location.hash = AccommodationTabs.PROPERTIES;
      setSelectedTab(AccommodationTabs.PROPERTIES);
    }
  }, []);

  useEffect(() => {
    if (!userIsAdmin && selectedTab === AccommodationTabs.DYNAMIC_CONFIGURATIONS) {
      setSelectedTab(AccommodationTabs.PROPERTIES);
      window.location.hash = AccommodationTabs.PROPERTIES;
    }
  }, [selectedTab, userIsAdmin]);

  const handleTabChange = useCallback(
    (event: SyntheticEvent, newValue: AccommodationTabs) => {
      setSelectedTab(newValue);
      push({
        search: '',
      });
      window.location.hash = newValue;
    },
    [push],
  );

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Accommodation - Properties</title>
      </Helmet>
      <PageHeader title="Accommodation - Properties">
        {isMakeMeAnOfferButtonVisible && <MakeMeAnOfferButton />}
      </PageHeader>
      <Stack direction="row" spacing={2} justifyContent="space-between">
        <Tabs sx={{ mb: 2 }} value={selectedTab} onChange={handleTabChange}>
          <Tab key={AccommodationTabs.PROPERTIES} value={AccommodationTabs.PROPERTIES} label="Properties" />
          <Tab
            key={AccommodationTabs.POTENTIAL_MAPPINGS}
            value={AccommodationTabs.POTENTIAL_MAPPINGS}
            label="Potential Mappings"
          />
          <Tab
            key={AccommodationTabs.UNMAPPED_SUPPLIERS}
            value={AccommodationTabs.UNMAPPED_SUPPLIERS}
            label="Unmapped Suppliers"
          />
          <Tab key={AccommodationTabs.FACILITIES} value={AccommodationTabs.FACILITIES} label="Facilities" />
          <Tab key={AccommodationTabs.AMENITIES} value={AccommodationTabs.AMENITIES} label="Amenities" />
          {isMmaoEnabled && (
            <Tab key={AccommodationTabs.MMAO_OFFERS} value={AccommodationTabs.MMAO_OFFERS} label="MMAO Offers" />
          )}
          <Tab key={AccommodationTabs.BUNDLES} value={AccommodationTabs.BUNDLES} label="Bundles" />
          {userIsAdmin && (
            <Tab
              key={AccommodationTabs.DYNAMIC_CONFIGURATIONS}
              value={AccommodationTabs.DYNAMIC_CONFIGURATIONS}
              label="Dynamic Configurations"
            />
          )}
        </Tabs>
      </Stack>
      <AccommodationProperties selectedTab={selectedTab} />
    </Container>
  );
}
